import { useState } from "react";
import { DeviceMobile, DeviceTablet, Desktop, Question } from "@phosphor-icons/react";
import { useSession } from "../auth/SessionProvider";
import {
  useRevokeOtherSessions,
  useSessionDevices,
  type SessionDevice
} from "../data/sessionQueries";
import { BoxState } from "../ui/BoxState";
import { Panel } from "../ui/Panel";
import "./clientSettings/settings.css";

/**
 * Inloggningar: where this account is signed in, and the one control that ends the rest of them.
 *
 * plan.md section 4 requires "View recent or active sessions and revoke other sessions", and section
 * 12 counts session review and session revocation among the five account paths that have to be
 * proven before the platform is complete. Neither existed. `public.record_session_device` had no
 * caller anywhere, so the table this panel reads was empty by construction and a list over it would
 * have told somebody signed in on four devices that they were signed in on one.
 *
 * **This is not `DevicePanel` and the two must not read as the same thing.** `Den här enheten` is
 * about the machine in front of the person: whether it shows notifications and whether the portal is
 * installed on it. This one is about the account: where it is signed in, and ending the sessions
 * that are not this one.
 *
 * **"This device" is decided here, at render time, and is never stored.** `schema.md` section 5:
 * "'Current device' is derived at request time by comparing `auth_session_id` with the JWT
 * `session_id`; it is not persisted as a boolean that can become stale." `useSession` reads that
 * claim out of the access token and the comparison below is the whole mechanism. A stored flag would
 * be right until the day two sessions both carried it.
 *
 * **No IP address and no location.** `schema.md` forbids storing one on this table and nothing here
 * asks for one. What a row carries is a form factor, a browser family and an operating system, which
 * is enough to answer "is one of these not mine" and not enough to identify a machine. The heading's
 * question mark says so, rather than leaving a person wondering why there is no city name.
 *
 * **The control is offered without a step-up, and that is a decision rather than an omission.**
 * plan.md section 8 requires recent re-authentication for four named actions: secret viewing,
 * cancellation, MFA changes and access changes. This is none of them, and the reason it is none of
 * them is that it removes access rather than granting it. Somebody holding a stolen session already
 * holds the account and gains nothing by ending the owner's other sessions; the owner signs back in
 * with a password the attacker does not have. A step-up here would stand between a frightened person
 * and the one control that answers their suspicion, on a device where their authenticator may not
 * be. The migration argues it at length on its own face.
 *
 * **One box since the second build round of 2026-09-24** (`prototype/kund/pages.js`, the
 * `inloggningar` section): a row per signed-in session, and the signed-out ones folded into the
 * box's foot beside the control. The lead above the list and the explanation under the control went
 * under design.md section 3's third rule; the privacy sentence is the heading's one line.
 */

const timeFormat = new Intl.DateTimeFormat("sv-SE", { dateStyle: "long", timeStyle: "short" });

/**
 * A timestamptz, formatted in the reader's own timezone.
 *
 * Unlike the `date` columns `ManagementPanel` formats, these carry an offset, so `new Date` parses
 * them to the right instant and there is no midnight-in-UTC trap to work around. An unparseable
 * value renders as null and the row says nothing about when instead of saying something wrong.
 */
function formatMoment(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : timeFormat.format(parsed);
}

/**
 * The icon for a row, chosen from the label the browser sent when the session was first seen.
 *
 * Decorative in every case: the same three words are printed next to it as text. A reader with no
 * icons loses nothing, which is what `design.md` means by shape and text carrying a state rather
 * than colour or an image.
 */
function DeviceIcon({ label }: { label: string | null }) {
  if (label === "Mobil") {
    return <DeviceMobile size={18} />;
  }
  if (label === "Surfplatta") {
    return <DeviceTablet size={18} />;
  }
  if (label === "Dator") {
    return <Desktop size={18} />;
  }
  return <Question size={18} />;
}

/**
 * One session as a sentence rather than as three fields.
 *
 * `Okänd` is what `describeDevice` answers for a user-agent string it cannot place, and it is
 * printed rather than hidden: a login this portal could not describe is exactly the one worth
 * looking twice at.
 */
function describe(device: SessionDevice): string {
  const parts = [
    device.device_label ?? "Okänd enhet",
    device.browser_family ?? "Okänd",
    device.operating_system ?? "Okänd"
  ];

  return `${parts[0]} · ${parts[1]} på ${parts[2]}`;
}

/**
 * The row that stands for every other session begun before device identity existed.
 *
 * Added 2026-09-19. Those sessions are real and still open, so they are listed: a session nobody
 * can see is the one worth ending. But the portal cannot tell which of them are the same machine,
 * so they are one row with a count rather than several rows that look like several devices. Each
 * becomes a row of its own the next time its device opens the portal, because
 * `public.record_session_device` gives the session's row the device key then.
 */
function isFolded(device: SessionDevice): boolean {
  return !device.ended && !device.identified && device.auth_session_id === null;
}

function SignedInRow({ device, current }: { device: SessionDevice; current: boolean }) {
  const first = formatMoment(device.first_seen_at);
  const last = formatMoment(device.last_seen_at);

  if (isFolded(device)) {
    return (
      <li className="k-session" data-testid="session-row" data-folded="true" data-ended="false">
        <span className="type-icon" aria-hidden="true">
          <Question size={18} />
        </span>
        <div className="k-session-facts">
          <h3>
            {device.login_count === 1
              ? "En äldre inloggning"
              : `${device.login_count} äldre inloggningar`}
          </h3>
          <p>
            De började innan portalen kände igen enheter, så de kan inte visas en och en. Var och en
            får en egen rad nästa gång den enheten öppnar portalen.
            {last ? ` Senast aktiv ${last}.` : ""}
          </p>
        </div>
      </li>
    );
  }

  return (
    <li className="k-session" data-testid="session-row" data-ended="false">
      <span className="type-icon" aria-hidden="true">
        <DeviceIcon label={device.device_label} />
      </span>
      <div className="k-session-facts">
        <h3>
          {describe(device)}
          {current ? (
            <span className="quiet k-session-mark" data-testid="session-current">
              den här
            </span>
          ) : null}
        </h3>
        <p>
          {last ? `Senast aktiv ${last}` : "Vi kan inte visa när den senast var aktiv"}
          {first ? ` · Inloggad sedan ${first}` : ""}
        </p>
      </div>
    </li>
  );
}

/*
  A closed session says so in words, next to its description, and not by colour or by being dimmed.
  design.md: colour is never the only status signal.
*/
function SignedOutRow({ device }: { device: SessionDevice }) {
  const last = formatMoment(device.last_seen_at);
  const endedAt = formatMoment(device.ended_at);

  return (
    <li className="k-session is-ended" data-testid="session-row" data-ended="true">
      <span className="type-icon" aria-hidden="true">
        <DeviceIcon label={device.device_label} />
      </span>
      <div className="k-session-facts">
        <h3>
          {describe(device)}
          <span className="quiet k-session-mark" data-testid="session-ended">
            Utloggad
          </span>
        </h3>
        <p>
          {last ? `Senast aktiv ${last}.` : "Vi kan inte visa när den senast var aktiv."}
          {device.login_count > 1 ? ` ${device.login_count} inloggningar.` : ""}
          {endedAt ? ` Avslutad ${endedAt}.` : " Avslutad, den kan inte användas längre."}
        </p>
      </div>
    </li>
  );
}

export function SessionsPanel() {
  const { status, authSessionId, signOut } = useSession();
  const devices = useSessionDevices(status === "signed-in");
  const revoke = useRevokeOtherSessions(() => signOut("others"));
  const [done, setDone] = useState<number | null>(null);

  const rows = devices.data ?? [];

  /*
    Two lists, and the split is the change of 2026-09-19. The person reading is looking for a device
    they do not recognise, so the list they read first holds only what is signed in, this session
    first. The past is kept, because it answers "was that closed?", but it is folded and the server
    has already grouped it so one machine stands once.
  */
  const isCurrent = (device: SessionDevice) =>
    !device.ended && authSessionId !== null && device.auth_session_id === authSessionId;
  const signedIn = rows
    .filter((device) => !device.ended)
    .sort((a, b) => Number(isCurrent(b)) - Number(isCurrent(a)));
  const signedOut = rows.filter((device) => device.ended);

  async function endOthers() {
    setDone(null);

    try {
      const marked = await revoke.mutateAsync();
      setDone(marked);
    } catch {
      // Rendered from the mutation's own error below. The sessions ended either way: the only
      // failure this call can have after `signOut` has returned is the marking, and its sentence
      // says so rather than inviting a person to think nothing happened.
    }
  }

  return (
    <Panel
      title="Inloggningar"
      titleId="sessions-title"
      hint="Portalen sparar ingen IP-adress och ingen plats."
      flush
      testId="sessions-panel"
      foot={
        <>
          {/*
            Closed by default and never removed. A native `<details>`, because it is keyboard and
            screen-reader operable without a line of script and states its own open or closed state.
            No count in the summary: the rows inside are the count (design.md section 3, rule 1).
          */}
          {!devices.isLoading && !devices.isError && signedOut.length > 0 ? (
            <details className="k-set-history" data-testid="sessions-history">
              <summary>Tidigare utloggningar</summary>
              <ul className="k-rows" data-testid="session-history-list">
                {signedOut.map((device) => (
                  <SignedOutRow key={device.id} device={device} />
                ))}
              </ul>
            </details>
          ) : null}

          {/*
            Rendered from the failure the mutation already built rather than run through
            `toPortalFailure` a second time. It returns a code and a sentence, and asking it to read a
            code out of its own Swedish sentence would answer "Något gick fel" for every refusal.
          */}
          {revoke.isError && revoke.error ? (
            <p className="form-error k-set-message" role="alert" data-testid="sessions-error">
              {revoke.error.message}
            </p>
          ) : null}

          {done !== null ? (
            <p className="form-success k-set-message" role="status" data-testid="sessions-revoked">
              {done === 0
                ? "Klart. Det fanns inga andra inloggningar att avsluta."
                : done === 1
                  ? "Klart. En annan inloggning är avslutad. Du är kvar här."
                  : `Klart. ${done} andra inloggningar är avslutade. Du är kvar här.`}
            </p>
          ) : null}

          {/*
            Offered whether or not the list shows another row, and that is deliberate. The list is
            what this portal has observed, and a session that signed in before the list existed is
            real and invisible. A button hidden because the panel sees nothing to end would be hiding
            the control in exactly the case somebody needs it.
          */}
          <button
            type="button"
            className="button is-quiet k-set-foot-act"
            disabled={revoke.isPending}
            onClick={() => void endOthers()}
          >
            {revoke.isPending ? "Avslutar" : "Logga ut andra enheter"}
          </button>
        </>
      }
    >
      {devices.isLoading ? (
        <BoxState kind="loading" title="Hämtar dina inloggningar" compact />
      ) : devices.isError ? (
        <BoxState
          kind="error"
          title="Vi kunde inte hämta dina inloggningar"
          body="Ingenting har ändrats. Försök igen."
          onRetry={() => void devices.refetch()}
          compact
        />
      ) : signedIn.length === 0 ? (
        /*
         * A real state and not an error. A session that signed in before this list existed has no
         * row, and so does one whose device call did not land. The sentence says what to do about
         * it, which is nothing: the next sign-in writes the row.
         */
        <BoxState
          kind="empty"
          title="Inga inloggningar att visa ännu"
          body="Den här inloggningen räknas från nästa gång du loggar in."
          compact
        />
      ) : (
        <ul className="k-rows" data-testid="session-list">
          {signedIn.map((device) => (
            <SignedInRow key={device.id} device={device} current={isCurrent(device)} />
          ))}
        </ul>
      )}
    </Panel>
  );
}
